import { type FormHTMLAttributes, forwardRef, type PropsWithChildren, type Ref, useCallback, useMemo } from 'react'
import {
  type FieldErrors,
  type FieldValues,
  type SubmitErrorHandler,
  type SubmitHandler,
  useFormContext,
} from 'react-hook-form'

import { addBreadcrumb } from '@sentry/react'
import { type Except } from 'type-fest'

import { AutomaticFormSubmitProvider } from './AutomaticFormSubmitProvider'
import { FormSubmitContext } from './FormSubmitContext'

export interface FormSubmitProviderProps<TFieldValues extends FieldValues = FieldValues> extends Except<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> {
  readonly autoSubmit?: boolean
  readonly resetOnSubmit?: boolean
  readonly autoSubmitDelay?: number
  readonly onFormSubmit: SubmitHandler<TFieldValues>
  readonly onFormApply?: SubmitHandler<TFieldValues>
  readonly onFormInvalid?: SubmitErrorHandler<TFieldValues>
}

export const FormSubmitProvider = forwardRef(
  <T extends FieldValues = FieldValues>(
    {
      onFormSubmit,
      onFormApply,
      onFormInvalid,
      autoComplete = 'off',
      autoSubmit = false,
      resetOnSubmit = false,
      autoSubmitDelay,
      children,
      ...props
    }: PropsWithChildren<FormSubmitProviderProps<T>>,
    ref: Ref<HTMLFormElement>
  ) => {
    const { handleSubmit, reset } = useFormContext<T>()
    const onFormInvalidWithBreadcrumb = useCallback(
      (errors: FieldErrors<T>) => {
        addBreadcrumb({
          category: 'form',
          message: 'invalid from data',
          level: 'info',
          data: { errors },
        })
        return onFormInvalid?.(errors)
      },
      [onFormInvalid]
    )
    const onFormSubmitWithBreadcrumb = useCallback(
      async (data: T) => {
        addBreadcrumb({
          category: 'form',
          message: 'submit',
          level: 'info',
          data: { data },
        })
        await onFormSubmit(data)
        if (resetOnSubmit) {
          reset()
        }
      },
      [onFormSubmit, reset, resetOnSubmit]
    )
    const onFormApplyWithBreadcrumb = useCallback(
      (data: T) => {
        addBreadcrumb({
          category: 'form',
          message: 'apply',
          level: 'info',
          data: { data },
        })
        return onFormApply?.(data)
      },
      [onFormApply]
    )

    const onSubmit = useMemo(
      () => handleSubmit(onFormSubmitWithBreadcrumb, onFormInvalidWithBreadcrumb),
      [handleSubmit, onFormSubmitWithBreadcrumb, onFormInvalidWithBreadcrumb]
    )
    const context = useMemo(
      () => ({
        submit: onSubmit,
        apply: onFormApply && handleSubmit(onFormApplyWithBreadcrumb, onFormInvalidWithBreadcrumb),
      }),
      [onSubmit, onFormApply, onFormApplyWithBreadcrumb, handleSubmit, onFormInvalidWithBreadcrumb]
    )
    return (
      <form autoComplete={autoComplete} onSubmit={onSubmit} ref={ref} {...props}>
        <FormSubmitContext.Provider value={context}>
          {autoSubmit ? (
            <AutomaticFormSubmitProvider wait={autoSubmitDelay}>{children}</AutomaticFormSubmitProvider>
          ) : (
            children
          )}
        </FormSubmitContext.Provider>
      </form>
    )
  }
)
